import { ColumnDef, Table } from "@tanstack/react-table";
import { Download, RefreshCw, X } from "lucide-react";
import { DataTable } from "@/components/data-table/data-table";

import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/custom/date-picker-with-range";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { DateRange } from "react-day-picker";
import { addDays, endOfDay, startOfDay } from "date-fns";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import z from "zod";
import { useUserContext } from "@/context/UserContext";
import { getCurrencySymbol } from "@/lib/utils";
import axiosInstance from "@/utils/axiosInstance";
import { DataTableFacetedFilterCustom } from "@/components/data-table/data-table-faceted-filter-custom";
import { downloadCSV } from "react-admin";
import { taxiStatusItems } from "@/constants/status";

export const TaxiReportData = () => {
  const { decodedToken } = useUserContext();
  const currency = getCurrencySymbol(
    decodedToken?.permissions?.currency || "ALL",
  );

  // date picker with range props
  const [loading, setLoading] = useState<boolean>(false);
  const [date, setDate] = useState<DateRange>({
    from: addDays(new Date(), -7),
    to: new Date(),
  });
  const [data, setData] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [columnVisibilityFilter, setColumnVisibilityFilter] = useState<
    string[]
  >(["status"]);

  const [groupBy, setGroupBy] = useState<string>("driver_id");

  // Source filter options with user-friendly labels
  const sourceOptions = [
    { label: "Client App", value: "CLIENT_APP" },
    { label: "Dispatch", value: "DISPATCH" },
    { label: "Taximeter", value: "TAXIMETER" },
    { label: "Web Booking", value: "WEB_BOOKING" },
    { label: "Phone Call", value: "PHONE_CALL" },
    { label: "API Integration", value: "API_INTEGRATION" },
    { label: "Partner Platform", value: "PARTNER_PLATFORM" },
    { label: "Admin App", value: "ADMIN_APP" },
    { label: "WhatsApp", value: "WHATSAPP" },
  ];

  // Column visibility options
  const columnVisibilityOptions = [
    { label: "Status Info", value: "status" },
    { label: "Source Info", value: "source" },
    decodedToken?.settings.settings_type !== "taxi" && {
      label: "Order Type",
      value: "order_type",
    },
  ].filter(Boolean);

  const taskSchema = z.object({
    groupValue: z.string(),
    groupLabel: z.string(),
    totalOrders: z.number(),
    totalRevenue: z.number().min(0),
    completedOrders: z.number().optional(),
    cancelledOrders: z.number().optional(),
    rejectedOrders: z.number().optional(),
    inProgressOrders: z.number().optional(),
    clientAppOrders: z.number().optional(),
    dispatchOrders: z.number().optional(),
    taximeterOrders: z.number().optional(),
    webBookingOrders: z.number().optional(),
    phoneCallOrders: z.number().optional(),
    apiIntegrationOrders: z.number().optional(),
    partnerPlatformOrders: z.number().optional(),
    adminAppOrders: z.number().optional(),
    whatsappOrders: z.number().optional(),
  });

  type Task = z.infer<typeof taskSchema>;

  // Helper functions for column visibility
  const shouldShowStatusColumns = () => {
    return groupBy !== "status" && columnVisibilityFilter.includes("status");
  };

  const shouldShowOrderTypeColumns = () => {
    return (
      groupBy !== "order_type" && columnVisibilityFilter.includes("order_type")
    );
  };

  const getVisibleStatusColumns = () => {
    if (!shouldShowStatusColumns()) return [];

    // If statusFilter is empty, show all status columns
    if (statusFilter.length === 0) {
      return ["completed", "cancelled", "rejected", "inProgress"];
    }

    // Otherwise, only show status columns that are in the filter
    const visibleColumns = [];
    if (statusFilter.includes("COMPLETED")) visibleColumns.push("completed");
    if (statusFilter.includes("CANCELLED")) visibleColumns.push("cancelled");
    if (statusFilter.includes("REJECTED")) visibleColumns.push("rejected");
    if (statusFilter.includes("IN_PROGRESS")) visibleColumns.push("inProgress");

    return visibleColumns;
  };

  const shouldShowSourceColumns = () => {
    return groupBy !== "source" && columnVisibilityFilter.includes("source");
  };

  const getVisibleSourceColumns = () => {
    if (!shouldShowSourceColumns()) return [];

    // If sourceFilter is empty, show all source columns
    if (sourceFilter.length === 0) {
      return [
        "clientApp",
        "dispatch",
        "taximeter",
        "webBooking",
        "phoneCall",
        "apiIntegration",
        "partnerPlatform",
        "adminApp",
        "whatsapp",
      ];
    }

    // Otherwise, only show source columns that are in the filter
    const visibleColumns = [];
    if (sourceFilter.includes("CLIENT_APP")) visibleColumns.push("clientApp");
    if (sourceFilter.includes("DISPATCH")) visibleColumns.push("dispatch");
    if (sourceFilter.includes("TAXIMETER")) visibleColumns.push("taximeter");
    if (sourceFilter.includes("WEB_BOOKING")) visibleColumns.push("webBooking");
    if (sourceFilter.includes("PHONE_CALL")) visibleColumns.push("phoneCall");
    if (sourceFilter.includes("API_INTEGRATION"))
      visibleColumns.push("apiIntegration");
    if (sourceFilter.includes("PARTNER_PLATFORM"))
      visibleColumns.push("partnerPlatform");
    if (sourceFilter.includes("ADMIN_APP")) visibleColumns.push("adminApp");
    if (sourceFilter.includes("WHATSAPP")) visibleColumns.push("whatsapp");

    return visibleColumns;
  };

  const getOrderTypeVisibleColumn = () => {
    if (!shouldShowOrderTypeColumns()) return [];
    return ["transfers", "tours"];
  };

  // Helper function to get group label title
  const getGroupLabelTitle = () => {
    switch (groupBy) {
      case "driver_id":
        return "Driver Name";
      case "vehicle_id":
        return "Vehicle";
      case "user_id":
        return "User";
      case "source":
        return "Source";
      case "status":
        return "Status";
      case "commission_partner_id":
        return "Partner Name";
      case "order_type":
        return "Order type";
      default:
        return "Group Label";
    }
  };

  const columns: ColumnDef<Task>[] = [
    {
      accessorKey: "groupValue",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="ID" />
      ),
      cell: ({ row }) => <div>{row.getValue("groupValue")}</div>,
    },
    {
      accessorKey: "groupLabel",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={getGroupLabelTitle()} />
      ),
      cell: ({ row }) => <div>{row.getValue("groupLabel")}</div>,
    },
    {
      accessorKey: "totalOrders",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Total Orders" />
      ),
      cell: ({ row }) => <div>{row.getValue("totalOrders")}</div>,
    },
    ...(shouldShowOrderTypeColumns()
      ? [
          {
            accessorKey: "transfers",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title={`Transfers`} />
            ),
            cell: ({ row }) => <p>{row.getValue("transfers")}</p>,
          },
          {
            accessorKey: "tours",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title={`Tours`} />
            ),
            cell: ({ row }) => <p>{row.getValue("tours")}</p>,
          },
        ]
      : []),
    {
      accessorKey: "totalRevenue",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={`Revenue ALL`} />
      ), // format to 2 decimal places, add thousand separator
      cell: ({ row }) => (
        <div>
          {Number(row.getValue("totalRevenue"))
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </div>
      ),
    },
    // Dynamic commission revenue
    ...(groupBy === "commission_partner_id"
      ? [
          {
            accessorKey: "commissionRevenue",
            header: ({ column }: any) => (
              <DataTableColumnHeader
                column={column}
                title={`Commission Revenue ALL`}
              />
            ), // format to 2 decimal places, add thousand separator
            cell: ({ row }: any) => (
              <div>
                {Number(row.getValue("commissionRevenue"))
                  .toFixed(2)
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </div>
            ),
          },
        ]
      : []),
    // Dynamically add status columns when groupBy is not 'status' AND status is in visibility filter
    ...(shouldShowStatusColumns()
      ? (() => {
          const visibleStatusColumns = getVisibleStatusColumns();
          const statusColumns = [];

          if (visibleStatusColumns.includes("completed")) {
            statusColumns.push({
              accessorKey: "completedOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Completed" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("completedOrders") || 0}</div>
              ),
            });
          }

          if (visibleStatusColumns.includes("cancelled")) {
            statusColumns.push({
              accessorKey: "cancelledOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Cancelled" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("cancelledOrders") || 0}</div>
              ),
            });
          }

          if (visibleStatusColumns.includes("rejected")) {
            statusColumns.push({
              accessorKey: "rejectedOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Rejected" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("rejectedOrders") || 0}</div>
              ),
            });
          }

          if (visibleStatusColumns.includes("inProgress")) {
            statusColumns.push({
              accessorKey: "inProgressOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Active" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("inProgressOrders") || 0}</div>
              ),
            });
          }

          return statusColumns;
        })()
      : []),
    // Dynamically add source columns when groupBy is not 'source' AND source is in visibility filter
    ...(shouldShowSourceColumns()
      ? (() => {
          const visibleSourceColumns = getVisibleSourceColumns();
          const sourceColumns = [];

          if (visibleSourceColumns.includes("clientApp")) {
            sourceColumns.push({
              accessorKey: "clientAppOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Client App" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("clientAppOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("dispatch")) {
            sourceColumns.push({
              accessorKey: "dispatchOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Dispatch" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("dispatchOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("taximeter")) {
            sourceColumns.push({
              accessorKey: "taximeterOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Taximeter" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("taximeterOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("webBooking")) {
            sourceColumns.push({
              accessorKey: "webBookingOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Web Booking" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("webBookingOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("phoneCall")) {
            sourceColumns.push({
              accessorKey: "phoneCallOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Phone Call" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("phoneCallOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("apiIntegration")) {
            sourceColumns.push({
              accessorKey: "apiIntegrationOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader
                  column={column}
                  title="API Integration"
                />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("apiIntegrationOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("partnerPlatform")) {
            sourceColumns.push({
              accessorKey: "partnerPlatformOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader
                  column={column}
                  title="Partner Platform"
                />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("partnerPlatformOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("adminApp")) {
            sourceColumns.push({
              accessorKey: "adminAppOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Admin App" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("adminAppOrders") || 0}</div>
              ),
            });
          }

          if (visibleSourceColumns.includes("whatsapp")) {
            sourceColumns.push({
              accessorKey: "whatsappOrders" as const,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="WhatsApp" />
              ),
              cell: ({ row }: any) => (
                <div>{row.getValue("whatsappOrders") || 0}</div>
              ),
            });
          }

          return sourceColumns;
        })()
      : []),
  ];

  // Function to render toolbar filter components - accepts table as parameter
  const renderToolbarFilters = (table: Table<Task>) => {
    return (
      <div className="flex items-center gap-3">
        {/* Hide status filter when groupBy is 'status' */}
        {groupBy !== "status" && (
          <DataTableFacetedFilterCustom
            title="Status"
            onChange={setStatusFilter}
            selectedValues={
              statusFilter ? new Set(statusFilter) : new Set<string>()
            }
            options={taxiStatusItems.map((item) => ({
              label: item.label,
              value: item.status,
            }))}
          />
        )}

        {/* Hide source filter when groupBy is 'source' */}
        {groupBy !== "source" && (
          <DataTableFacetedFilterCustom
            title="Source"
            onChange={setSourceFilter}
            selectedValues={
              sourceFilter ? new Set(sourceFilter) : new Set<string>()
            }
            options={sourceOptions}
          />
        )}

        {/* Column Visibility Filter */}
        <DataTableFacetedFilterCustom
          title="Columns"
          onChange={(values) => {
            setColumnVisibilityFilter(values);
          }}
          selectedValues={
            columnVisibilityFilter
              ? new Set(columnVisibilityFilter)
              : new Set<string>()
          }
          options={columnVisibilityOptions.filter((option) => {
            // Hide status option when grouping by status, hide source option when grouping by source
            if (option.value === "status" && groupBy === "status") return false;
            if (option.value === "source" && groupBy === "source") return false;
            return true;
          })}
        />

        {/* Reset Filters Button - Show only when filters are active */}
        {(statusFilter.length > 0 || sourceFilter.length > 0) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStatusFilter([]);
              setSourceFilter([]);
            }}
            className="h-10"
          >
            Reset
            <X />
          </Button>
        )}
      </div>
    );
  };

  // CSV Download function
  const handleDownloadCSV = () => {
    if (data.length === 0) {
      console.warn("No data to download");
      return;
    }

    // Build CSV data structure that matches exactly the visible columns
    const csvData = data.map((item) => {
      const rowData: Record<string, any> = {};

      // Always include base columns (these are always visible)
      rowData["Group ID"] = item.groupValue;
      rowData[getGroupLabelTitle()] = item.groupLabel;
      rowData["Total Orders"] = item.totalOrders;
      rowData[`Total Revenue (${currency})`] = Number(
        item.totalRevenue,
      ).toFixed(2);

      if (groupBy === "commission_partner_id")
        rowData[`Commission Revenue (${currency})`] = Number(
          item.commissionRevenue,
        ).toFixed(2);

      // Add status columns only if they are currently visible in the table
      if (shouldShowStatusColumns()) {
        const visibleStatusColumns = getVisibleStatusColumns();

        if (visibleStatusColumns.includes("completed")) {
          rowData["Completed Orders"] = item.completedOrders || 0;
        }
        if (visibleStatusColumns.includes("cancelled")) {
          rowData["Cancelled Orders"] = item.cancelledOrders || 0;
        }
        if (visibleStatusColumns.includes("rejected")) {
          rowData["Rejected Orders"] = item.rejectedOrders || 0;
        }
        if (visibleStatusColumns.includes("inProgress")) {
          rowData["In Progress Orders"] = item.inProgressOrders || 0;
        }
      }

      // Add source columns only if they are currently visible in the table
      if (shouldShowSourceColumns()) {
        const visibleSourceColumns = getVisibleSourceColumns();

        if (visibleSourceColumns.includes("clientApp")) {
          rowData["Client App Orders"] = item.clientAppOrders || 0;
        }
        if (visibleSourceColumns.includes("dispatch")) {
          rowData["Dispatch Orders"] = item.dispatchOrders || 0;
        }
        if (visibleSourceColumns.includes("taximeter")) {
          rowData["Taximeter Orders"] = item.taximeterOrders || 0;
        }
        if (visibleSourceColumns.includes("webBooking")) {
          rowData["Web Booking Orders"] = item.webBookingOrders || 0;
        }
        if (visibleSourceColumns.includes("phoneCall")) {
          rowData["Phone Call Orders"] = item.phoneCallOrders || 0;
        }
        if (visibleSourceColumns.includes("apiIntegration")) {
          rowData["API Integration Orders"] = item.apiIntegrationOrders || 0;
        }
        if (visibleSourceColumns.includes("partnerPlatform")) {
          rowData["Partner Platform Orders"] = item.partnerPlatformOrders || 0;
        }
        if (visibleSourceColumns.includes("adminApp")) {
          rowData["Admin App Orders"] = item.adminAppOrders || 0;
        }
        if (visibleSourceColumns.includes("whatsapp")) {
          rowData["WhatsApp Orders"] = item.whatsappOrders || 0;
        }
      }

      return rowData;
    });

    // Generate filename with current date and filters
    const dateStr = new Date().toISOString().split("T")[0];
    const groupByLabel = getGroupLabelTitle().replace(" ", "_").toLowerCase();
    const filename = `taxi_report_${groupByLabel}_${dateStr}`;

    // Convert to CSV format - headers will match exactly what's visible in the table
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        headers.map((header) => `"${row[header]}"`).join(","),
      ),
    ].join("\n");

    downloadCSV(csvContent, filename);
  };

  // Add new order function
  const loadReportData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/reports-taxi/generate-report",
        {
          startDate:
            date.from?.toISOString() ?? startOfDay(new Date()).toISOString(),
          endDate: date.to?.toISOString() ?? endOfDay(new Date()).toISOString(),
          groupBy,
          status: statusFilter,
          source: sourceFilter,
        },
      );
      setData(response.data);
    } catch (error) {
      console.error("Error loading report data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load data when dependencies change
  useEffect(() => {
    // Only load if we have a valid date range
    if (date.from && date.to) {
      loadReportData();
    }
  }, [date, groupBy, statusFilter, sourceFilter]);

  return (
    <div className="p-5">
      {/* Date Range, Group By Controls, and Title */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="dateRange" className="text-sm">
              Date Range
            </Label>
            <DatePickerWithRange
              title="Filter by created date"
              date={date}
              setDate={(dateRange) => {
                if (dateRange) {
                  setDate(dateRange);
                }
              }}
            />
          </div>

          <div className="flex flex-col gap-2 mb-2">
            <Label htmlFor="groupBy" className="text-sm">
              Group By
            </Label>
            <Select
              value={groupBy}
              onValueChange={(value) => {
                setGroupBy(value);
                // Clear filters when they match the group by selection
                if (value === "status") {
                  setStatusFilter([]);
                  // Remove status from column visibility when grouping by status
                  setColumnVisibilityFilter((prev) =>
                    prev.filter((item) => item !== "status"),
                  );
                }
                if (value === "source") {
                  setSourceFilter([]);
                  // Remove source from column visibility when grouping by source
                  setColumnVisibilityFilter((prev) =>
                    prev.filter((item) => item !== "source"),
                  );
                }
              }}
            >
              <SelectTrigger className="w-[180x]">
                <SelectValue placeholder="Select grouping" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="driver_id">Driver</SelectItem>
                <SelectItem value="vehicle_id">Vehicle</SelectItem>
                {decodedToken?.settings.settings_type ===
                  "transportation_agency" && (
                  <>
                    <SelectItem value="commission_partner_id">
                      Partner
                    </SelectItem>
                    <SelectItem value="order_type">Order type</SelectItem>
                  </>
                )}
                {decodedToken?.settings.settings_type === "taxi" && (
                  <SelectItem value="user_id">User</SelectItem>
                )}
                <SelectItem value="source">Source</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* <h2 className="text-md font-bold">Taxi Order Reports</h2> */}
      </div>

      <DataTable
        data={data}
        columns={columns}
        renderToolbarFilters={renderToolbarFilters}
        rightActions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadReportData}
              className="h-8"
              disabled={loading}
            >
              <RefreshCw className={`${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={data.length === 0}
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>
        }
      />
    </div>
  );
};

export default TaxiReportData;
